// Fila "Tabelas enviadas" do admin Everlog (PO1-2312), no molde da
// homologação da jornada: filtro de status, lista paginada e modal de detalhe
// com os arquivos e os eventos. Ações: Iniciar (em cadastro), Vincular a uma
// FreteTabela INATIVA já cadastrada (copia os arquivos para o anexo da tabela
// e avisa o embarcador) e Devolver com motivo. A ativação é do embarcador.
import React, { useCallback, useEffect, useState } from "react";
import { Button, ButtonGroup, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, Spinner, Table } from "reactstrap";

import PageHero, { PageHeroRefreshButton } from "components/PageHero";
import FilterBand from "components/FilterBand";
import ShellCard from "components/ShellCard";
import SectionCard from "components/SectionCard";
import TablePagination from "components/TablePagination";
import { Badge } from "components/ChecklistRow";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { useToast } from "hooks/toast";
import {
  listarTabelasEnviadas,
  detalheTabelaEnviada,
  urlArquivoTabelaEnviada,
  tabelasCandidatas,
  iniciarTabelaEnviada,
  vincularTabelaEnviada,
  devolverTabelaEnviada,
} from "services/tabelasEnviadasAdminApi";
import { mensagemErro } from "services/meuFreteApi";
import { formatDocument } from "utils/formatDoc";
import { formatDateTime } from "utils/formatDate";

const STATUS_OPTIONS = [
  { value: "pendente", label: "Pendentes" },
  { value: "cadastrada", label: "Aguardando embarcador" },
  { value: "aprovada", label: "Aprovadas" },
  { value: "devolvida", label: "Devolvidas" },
  { value: "todos", label: "Todas" },
];
const ESTADO = {
  RECEBIDA: { label: "Recebida", tipo: "auto" },
  EM_CADASTRO: { label: "Em cadastro", tipo: "auto" },
  CADASTRADA: { label: "Aguardando embarcador", tipo: "pend" },
  APROVADA: { label: "Aprovada", tipo: "done" },
  DEVOLVIDA: { label: "Devolvida", tipo: "lock" },
};
const EVENTO = { ENVIADA: "Enviada pela transportadora", INICIADA: "Cadastro iniciado", CADASTRADA: "Tabela vinculada", DEVOLVIDA: "Devolvida", APROVADA: "Aprovada pelo embarcador" };
const PAGE_SIZES = [10, 20, 50, 100];
const mutedStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem" };
const stateBoxStyle = { display: "flex", alignItems: "center", gap: "0.6rem", padding: "1rem 0.25rem", color: AUTOPILOT_COLORS.muted, fontSize: "0.9rem" };

function fmtVigencia(item) {
  const d = (v) => (v ? String(v).slice(0, 10).split("-").reverse().join("/") : "");
  if (item.vigenciaInicio && item.vigenciaFim) return `${d(item.vigenciaInicio)} a ${d(item.vigenciaFim)}`;
  if (item.vigenciaInicio) return `a partir de ${d(item.vigenciaInicio)}`;
  if (item.vigenciaFim) return `até ${d(item.vigenciaFim)}`;
  return "—";
}

export default function TabelasEnviadasAdmin() {
  const { addToast } = useToast();
  const [status, setStatus] = useState("pendente");
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [agindo, setAgindo] = useState(null);

  const [detalhe, setDetalhe] = useState(null); // { item, arquivos, eventos }
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);
  const [devolver, setDevolver] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [vincular, setVincular] = useState(null);
  const [candidatas, setCandidatas] = useState([]);
  const [idTabela, setIdTabela] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const r = await listarTabelasEnviadas({ status, q: q || undefined, page, pageSize });
      setData({ items: r.items || [], total: Number(r.total) || 0 });
    } catch (err) {
      const msg = mensagemErro(err);
      setErro(msg);
      addToast({ place: "tr", type: "danger", title: "Erro ao listar a fila", description: msg });
    } finally {
      setLoading(false);
    }
  }, [status, q, page, pageSize, addToast]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrirDetalhe = async (row) => {
    setLoadingDetalhe(true);
    setDetalhe({ item: row, arquivos: [], eventos: [] });
    try {
      setDetalhe(await detalheTabelaEnviada(row.id));
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Erro ao abrir o item", description: mensagemErro(err) });
    } finally {
      setLoadingDetalhe(false);
    }
  };

  const abrirArquivo = async (item, arquivo) => {
    try {
      const { url } = await urlArquivoTabelaEnviada(item.id, arquivo.id);
      window.open(url, "_blank", "noopener");
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível abrir o arquivo", description: mensagemErro(err) });
    }
  };

  const iniciar = async (row) => {
    setAgindo(row.id);
    try {
      await iniciarTabelaEnviada(row.id);
      addToast({ place: "tr", type: "success", title: "Em cadastro", description: "Cadastre a tabela em Tabelas de Frete (ela nasce inativa) e depois clique em Vincular." });
      carregar();
      if (detalhe && detalhe.item.id === row.id) abrirDetalhe(row);
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível iniciar", description: mensagemErro(err) });
    } finally {
      setAgindo(null);
    }
  };

  const abrirVincular = async (row) => {
    setVincular(row);
    setIdTabela("");
    setCandidatas([]);
    try {
      const r = await tabelasCandidatas(row.id);
      setCandidatas(r.tabelas || []);
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Erro ao listar as tabelas candidatas", description: mensagemErro(err) });
    }
  };

  const confirmarVincular = async () => {
    if (!vincular || !idTabela) return;
    setAgindo(vincular.id);
    try {
      await vincularTabelaEnviada(vincular.id, { idFreteTabela: Number(idTabela) });
      addToast({ place: "tr", type: "success", title: "Tabela vinculada", description: "Os usuários do embarcador foram avisados para aprovar; a transportadora também." });
      setVincular(null);
      carregar();
      setDetalhe(null);
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível vincular", description: mensagemErro(err) });
    } finally {
      setAgindo(null);
    }
  };

  const confirmarDevolver = async () => {
    if (!devolver || !motivo.trim()) return;
    setAgindo(devolver.id);
    try {
      await devolverTabelaEnviada(devolver.id, { motivo: motivo.trim() });
      addToast({ place: "tr", type: "success", title: "Devolvida", description: "A transportadora vê o motivo no MeuFrete e foi avisada por e-mail." });
      setDevolver(null);
      setMotivo("");
      carregar();
      setDetalhe(null);
    } catch (err) {
      addToast({ place: "tr", type: "danger", title: "Não foi possível devolver", description: mensagemErro(err) });
    } finally {
      setAgindo(null);
    }
  };

  const filtrosBotoes = (
    <ButtonGroup size="sm" aria-label="Filtro de status">
      {STATUS_OPTIONS.map((opt) => (
        <Button key={opt.value} color="info" outline={opt.value !== status} onClick={() => { setStatus(opt.value); setPage(1); }} aria-pressed={opt.value === status}>
          {opt.label}
        </Button>
      ))}
    </ButtonGroup>
  );

  const podeAgir = (row) => row.estado === "RECEBIDA" || row.estado === "EM_CADASTRO";

  return (
    <div className="content">
      <PageHero
        eyebrow="MeuFrete"
        title="Tabelas enviadas"
        description="Tabelas de preço que as transportadoras subiram pelo MeuFrete. Cadastre a tabela em Tabelas de Frete (ela nasce inativa), vincule aqui e o embarcador aprova; a Everlog não ativa."
        actions={<PageHeroRefreshButton onClick={() => carregar()} loading={loading} />}
      />

      <FilterBand
        mode="server"
        searchLabel="Buscar"
        searchHint="Transportadora (fantasia, razão social, CNPJ), embarcador ou nome da tabela"
        searchPlaceholder="Digite para buscar"
        searchValue={qInput}
        onSearchChange={setQInput}
        onSearchSubmit={(valor) => { setQ(String(valor || "").trim()); setPage(1); }}
        showFiltersButton={false}
        showActions={false}
        extra={filtrosBotoes}
      />

      <ShellCard>
        <SectionCard title="Fila" description="Clique na linha para ver os arquivos e o histórico." action={<span style={{ ...mutedStyle, whiteSpace: "nowrap" }}>{data.total} resultado(s)</span>}>
          {loading ? (
            <div style={stateBoxStyle}><Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} /><span>Carregando…</span></div>
          ) : erro ? (
            <div style={stateBoxStyle} role="alert">Não foi possível carregar a lista: {erro}</div>
          ) : data.items.length === 0 ? (
            <div style={stateBoxStyle}>Nenhum item neste filtro.</div>
          ) : (
            <>
              <Table size="sm" responsive hover className="hub-thead-accent">
                <thead className="text-primary">
                  <tr><th>Enviada em</th><th>Transportadora</th><th>Embarcador</th><th>Vigência</th><th>Arquivos</th><th>Situação</th><th /></tr>
                </thead>
                <tbody>
                  {data.items.map((row) => {
                    const est = ESTADO[row.estado] || { label: row.estado, tipo: "auto" };
                    return (
                      <tr key={row.id} style={{ cursor: "pointer" }} onClick={() => abrirDetalhe(row)}>
                        <td style={{ whiteSpace: "nowrap" }}>{formatDateTime(row.creationTime)}</td>
                        <td>
                          <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>{row.transportadora}</div>
                          <div style={mutedStyle}>{formatDocument(row.transportadoraCnpj)}</div>
                        </td>
                        <td>{row.embarcador}</td>
                        <td>{fmtVigencia(row)}</td>
                        <td style={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.arquivos || ""}>{row.arquivos || "—"}</td>
                        <td>
                          <Badge tipo={est.tipo}>{est.label}</Badge>
                          {row.tabelaNome && <div style={mutedStyle}>{row.tabelaNome} (#{row.idFreteTabela})</div>}
                          {row.estado === "DEVOLVIDA" && row.motivoDevolucao && <div style={mutedStyle}>{row.motivoDevolucao}</div>}
                        </td>
                        <td className="text-right" style={{ whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                          {row.estado === "RECEBIDA" && (
                            <Button size="sm" color="info" outline disabled={agindo === row.id} onClick={() => iniciar(row)}>Iniciar</Button>
                          )}{" "}
                          {podeAgir(row) && (
                            <>
                              <Button size="sm" color="success" outline disabled={agindo === row.id} onClick={() => abrirVincular(row)}>Vincular</Button>{" "}
                              <Button size="sm" color="warning" outline disabled={agindo === row.id} onClick={() => { setDevolver(row); setMotivo(""); }}>Devolver</Button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <TablePagination page={page} pageSize={pageSize} total={data.total} pageSizes={PAGE_SIZES} itemLabel="item(ns)" onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(1); }} />
            </>
          )}
        </SectionCard>
      </ShellCard>

      {/* Detalhe */}
      <Modal isOpen={!!detalhe} toggle={() => setDetalhe(null)} modalClassName="modal-align-top" fade={false} size="lg">
        <ModalBody style={{ padding: "1.5rem" }}>
          {detalhe && (
            <>
              <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "0.95rem", fontWeight: 600 }}>
                Envio #{detalhe.item.id} · {detalhe.item.transportadora} → {detalhe.item.embarcador}
              </div>
              <div style={{ ...mutedStyle, marginBottom: "0.75rem" }}>
                Vigência {fmtVigencia(detalhe.item)}{detalhe.item.observacao ? ` · Observação: ${detalhe.item.observacao}` : ""}
              </div>
              {loadingDetalhe ? (
                <div style={stateBoxStyle}><Spinner size="sm" /><span>Carregando…</span></div>
              ) : (
                <>
                  <SectionCard variant="field" eyebrow="Arquivos" style={{ marginBottom: "0.75rem" }}>
                    {(detalhe.arquivos || []).length === 0 ? (
                      <div style={mutedStyle}>Nenhum arquivo.</div>
                    ) : (
                      detalhe.arquivos.map((a) => (
                        <div key={a.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.3rem 0" }}>
                          <span>{a.nome} <span style={mutedStyle}>({a.formato}, {Math.round((a.tamanho || 0) / 1024)} KB)</span></span>
                          <Button size="sm" color="info" outline onClick={() => abrirArquivo(detalhe.item, a)}>Abrir</Button>
                        </div>
                      ))
                    )}
                  </SectionCard>
                  <SectionCard variant="field" eyebrow="Histórico">
                    {(detalhe.eventos || []).map((e) => (
                      <div key={e.id} style={{ padding: "0.3rem 0", fontSize: "0.84rem" }}>
                        <strong style={{ color: AUTOPILOT_COLORS.title }}>{EVENTO[e.tipo] || e.tipo}</strong>
                        <span style={mutedStyle}> · {formatDateTime(e.creationTime)}{e.userNome ? ` · ${e.userNome} ${e.userSobrenome || ""}` : ""}</span>
                        {e.motivo && <div style={mutedStyle}>Motivo: {e.motivo}</div>}
                        {e.detalhe && <div style={mutedStyle}>{e.detalhe}</div>}
                      </div>
                    ))}
                  </SectionCard>
                </>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {detalhe && detalhe.item.estado === "RECEBIDA" && <Button color="info" outline onClick={() => iniciar(detalhe.item)} disabled={!!agindo}>Iniciar cadastro</Button>}
          {detalhe && podeAgir(detalhe.item) && (
            <>
              <Button color="success" outline onClick={() => abrirVincular(detalhe.item)} disabled={!!agindo}>Vincular tabela</Button>
              <Button color="warning" outline onClick={() => { setDevolver(detalhe.item); setMotivo(""); }} disabled={!!agindo}>Devolver</Button>
            </>
          )}
          <Button color="secondary" outline onClick={() => setDetalhe(null)}>Fechar</Button>
        </ModalFooter>
      </Modal>

      {/* Vincular */}
      <Modal isOpen={!!vincular} toggle={agindo ? undefined : () => setVincular(null)} modalClassName="modal-align-top" fade={false}>
        <ModalBody style={{ padding: "1.5rem" }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "0.95rem", fontWeight: 600, marginBottom: "0.35rem" }}>Vincular à tabela cadastrada</div>
          <div style={{ ...mutedStyle, marginBottom: "1rem" }}>
            Só aparecem tabelas <strong>inativas</strong> do embarcador {vincular && vincular.embarcador}, da transportadora {vincular && vincular.transportadora} (ou uma filial da mesma raiz), ainda sem envio ligado. Os arquivos do envio são copiados como anexo da tabela.
          </div>
          <FormGroup>
            <Label style={{ color: AUTOPILOT_COLORS.text }}>Tabela *</Label>
            <Input type="select" value={idTabela} onChange={(e) => setIdTabela(e.target.value)}>
              <option value="">{candidatas.length ? "Selecione" : "Nenhuma tabela inativa candidata; cadastre-a em Tabelas de Frete"}</option>
              {candidatas.map((t) => (
                <option key={t.id} value={t.id}>#{t.id} · {t.nome} · {t.transportadora || ""}</option>
              ))}
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setVincular(null)} disabled={!!agindo}>Cancelar</Button>
          <Button color="success" onClick={confirmarVincular} disabled={!!agindo || !idTabela}>{agindo ? <Spinner size="sm" /> : "Vincular"}</Button>
        </ModalFooter>
      </Modal>

      {/* Devolver */}
      <Modal isOpen={!!devolver} toggle={agindo ? undefined : () => setDevolver(null)} modalClassName="modal-align-top" fade={false}>
        <ModalBody style={{ padding: "1.5rem" }}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "0.95rem", fontWeight: 600, marginBottom: "0.35rem" }}>Devolver para a transportadora</div>
          <div style={{ ...mutedStyle, marginBottom: "1rem" }}>
            {devolver && `${devolver.transportadora} → ${devolver.embarcador}. `}A transportadora vê o motivo no MeuFrete, corrige e envia de novo.
          </div>
          <FormGroup>
            <Label style={{ color: AUTOPILOT_COLORS.text }}>Motivo * (até 500 caracteres)</Label>
            <Input type="textarea" rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value.slice(0, 500))} />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setDevolver(null)} disabled={!!agindo}>Cancelar</Button>
          <Button color="warning" onClick={confirmarDevolver} disabled={!!agindo || !motivo.trim()}>{agindo ? <Spinner size="sm" /> : "Devolver"}</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
